import React, { useEffect, useReducer, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MenuItem, MenuList } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfirmDeleteDialog, MoveDialog, SaveFileDialog } from "..";
import {
	CustomDialog,
	CustomMenuButton,
	CustomTooltip,
	HorizontalDivider,
	icon,
	IconComponent,
	SidePanel,
	SidePanelContent,
	ViewTabPanel,
	ViewTabs,
} from "../../../../common/components";
import { NavUtils } from "../../../../navigation";
import DocumentCenterPanelTitle from "./document-center-panel-title/DocumentCenterPanelTitle";
import { DocumentCenterDocViewerTab, DocumentCenterInformationTab, DocumentCenterVersionTab } from "./tabs";
import styles from "./DocumentCenterSidePanel.module.css";
import { useApi, useTrackEvent } from "../../../../common/hooks";
import { ApiService, ProjectDirectoryService, ProjectDocumentService } from "../../../../api";
import {
	getContent,
	getTree,
	ROOT_DIR,
	setHasReceivedModifications,
	setOpenUploadDialog,
	setRefreshSelectedDocument,
} from "../../slice/document-center-slice";
import {
	compareInsensitiveCaseString,
	downloadFile,
	endsWithOneOf,
	isDisplayableFileType,
} from "../../../../common/utils";
import {
	hasPermission,
	environmentAllowed,
	environmentSwitch,
	isSegFeatureEnabled,
	SegFlags,
	translate,
} from "../../../../common/providers";
import { onBeforeUnloadHandler } from "../../utils/utils";
import { SOURCE_TYPES } from "../../../../common/constants";
import UnsupportedDocumentBanner from "./UnsupportedDocumentBanner";
import DeleteLastVersionDialog from "../delete-last-version-dialog/DeleteLastVersionDialog";
import { FILE_TYPES, FILE_TYPES_EXTENSIONS } from "../../../../common/constants/file-types";
import { DOCUMENT_STATUS } from "../../constants/constants";
import permissions from "../../../../common/providers/permissions/permissions";

const queryParam = "directory";

export default function DocumentCenterSidePanel({ accessTab = 0, id, onClose, open, status, type }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const trackEvent = useTrackEvent();
	const [selectedTab, setSelectedTab] = useState(0);
	const [downloading, setDownloading] = useState(false);
	const { call: onUploadNewVersion } = useApi(ProjectDocumentService.uploadNewVersion);
	const { call: onDownload } = useApi(ProjectDirectoryService.download);
	const { call: onMove } = useApi(ProjectDirectoryService.moveDirectoryContent);
	const { call: onDelete } = useApi(ProjectDirectoryService.deleteSelection);
	const [openRenameDialog, setOpenRenameDialog] = useState(false);
	const [directoryId, setDirectoryId] = useState(null);
	const [openMoveDialog, dispatchOpenMoveDialog] = useReducer((prev) => !prev, false);
	const [openConfirmDeleteDialog, dispatchOpenConfirmDeleteDialog] = useReducer((prev) => !prev, false);
	const [openDeleteLastVersionDialog, dispatchOpenDeleteLastVersionDialog] = useReducer((prev) => !prev, false);
	const [selectedDocInformation, setSelectedDocInformation] = useState(null);
	const [isEditingMetadata, setIsEditingMetadata] = useState(false);
	const projectId = useSelector(({ context }) => context.project.id);
	const currentDirectoryId = useSelector(({ documentCenter }) => documentCenter.currentDirectoryId);
	const hasActionPermission = useSelector(({ documentCenter }) => documentCenter.hasActionPermission);
	const defaultItem = { label: "", version: 1 };
	const selectedDocument = useSelector(({ documentCenter }) =>
		documentCenter.currentDirectoryContent.contents?.find(
			(c) =>
				!!type &&
				c.id === id &&
				((c.type?.toUpperCase() === type?.toUpperCase() && isDisplayableFileType(type.toUpperCase())) ||
					(type === "TENDER" && c.docType === type))
		)
	);
	const { label, version, docType } = selectedDocument || defaultItem;
	const isUnsupported = useMemo(() => compareInsensitiveCaseString(type, FILE_TYPES.UNSUPPORTED), [type]);
	const isOngoing = useMemo(
		() => status === DOCUMENT_STATUS.IN_PROGRESS || status === DOCUMENT_STATUS.PENDING,
		[status]
	);
	const isOkayFile = useMemo(
		() => ![DOCUMENT_STATUS.FAILED, DOCUMENT_STATUS.NOT_MANAGED].some((docStatus) => docStatus === status),
		[status]
	);
	const redirectUrl = useMemo(() => {
		if (status === "COMPLETED") {
			return NavUtils.goToSmartReview({
				projectId,
				documentId: id,
				source: SOURCE_TYPES.DOC_CENTER,
			});
		}
		return directoryId
			? `/projects/${projectId}/documents?${queryParam}=${directoryId}`
			: `/projects/${projectId}/documents`;
	}, [directoryId, id, projectId, status]);
	const menuListRef = useRef(null);
	const fileInputRef = useRef(null);
	const dropDownControlRef = useRef(null);

	const tabs = useMemo(() => {
		const documentTab = {
			key: "document",
			label: translate("document-center.side-panel.document"),
			iconName: icon.faFileAlt,
		};
		const informationTab = {
			key: "information",
			label: translate("document-center.side-panel.information"),
			iconName: icon.faInfoCircle,
		};
		const versionsTab = {
			key: "versions",
			label: translate("document-center.side-panel.versions"),
			iconName: icon.faHistory,
		};
		return type === "folder"
			? [informationTab]
			: [...(isUnsupported ? [] : [documentTab]), informationTab, ...(isOkayFile ? [versionsTab] : [])];
	}, [type, isOkayFile, isUnsupported]);
	const documentTabIndex = tabs.findIndex((tab) => tab.key === "document");
	const informationTabIndex = tabs.findIndex((tab) => tab.key === "information");
	const versionsTabIndex = tabs.findIndex((tab) => tab.key === "versions");

	useEffect(() => () => setSelectedTab(0), []);
	useEffect(() => setSelectedTab(accessTab ?? 0), [accessTab]);
	useEffect(() => {
		const handleCloseMenuList = (event) => {
			if (menuListRef.current && !menuListRef.current.contains(event.target)) {
				dropDownControlRef.current?.onClose?.();
			}
		};

		document.addEventListener("mousedown", handleCloseMenuList);
		return () => {
			document.removeEventListener("mousedown", handleCloseMenuList);
		};
	}, []);
	const handleOnCloseMenu = () => {
		if (dropDownControlRef.current?.onClose) {
			dropDownControlRef.current.onClose?.();
		}
	};
	const handleOpenRenameDialog = () => {
		setIsEditingMetadata(false);
		setOpenRenameDialog(true);
		handleOnCloseMenu();
	};
	const handleCloseRenameDialog = () => {
		setOpenRenameDialog(false);
	};
	const handleUploadVersion = (e) => {
		handleOnCloseMenu();
		if (!hasActionPermission) {
			return;
		}
		const targetFiles = e.target.files;
		const file = Object.keys(targetFiles).map((key) => targetFiles[key])[0];
		if (!file) {
			return;
		}
		window.addEventListener("beforeunload", onBeforeUnloadHandler);
		onUploadNewVersion({ prevDocumentId: id }, file, null)
			.then(() => {
				dispatch(setHasReceivedModifications(true));
				trackEvent("Document_Manual_V2_Upload");
			})
			.finally(() => {
				window.removeEventListener("beforeunload", onBeforeUnloadHandler);
			});
		dispatch(setOpenUploadDialog(true));
	};
	const handleRedirectToCanvas = () => {
		if (isSegFeatureEnabled(SegFlags.RIGHTS_ON_DOCUMENTS)) {
			navigate(NavUtils.goToRightsOnDocuments(projectId, id));
		}
	};
	const handleDownload = () => {
		handleOnCloseMenu();
		setDownloading(true);
		onDownload(
			{ projectId },
			{
				fromDirectoryId: currentDirectoryId,
				directoryIds: type === "folder" ? [id] : [],
				documentIds: type === "folder" ? [] : [id],
			}
		)
			.then(({ data, filename }) => downloadFile({ data, filename, filetype: "zip" }))
			.catch((err) => console.error(err))
			.finally(() => setDownloading(false));
	};
	const handleClose = () => {
		setSelectedTab(0);
		onClose();
		dispatch(setRefreshSelectedDocument(true));
	};
	const resetTreeAndContent = () => {
		handleClose();
		const { token } = ApiService.getCancelTokenSource();
		dispatch(getTree({ projectId, token }));
		dispatch(getContent({ projectId, directoryId: currentDirectoryId, token }));
	};
	const handleMove = (selectedDirectoryId) => {
		dispatchOpenMoveDialog();
		onMove(
			{ projectId },
			{
				toDirectoryId: selectedDirectoryId === ROOT_DIR ? null : selectedDirectoryId,
				fromDirectoryId: currentDirectoryId,
				directoryIds: type === "folder" ? [id] : [],
				documentIds: type === "folder" ? [] : [id],
			}
		)
			.then(resetTreeAndContent)
			.catch(console.error);
	};
	const handleOpenMoveDialog = () => {
		handleOnCloseMenu();
		dispatchOpenMoveDialog();
	};
	const handleDelete = (deleteContent) => {
		if (type === "folder") {
			dispatchOpenConfirmDeleteDialog();
		}
		onDelete(
			{ projectId },
			{
				fromDirectoryId: currentDirectoryId,
				deleteContent,
				directoryIds: type === "folder" ? [id] : [],
				documentIds: type === "folder" ? [] : [id],
			}
		)
			.then(resetTreeAndContent)
			.catch(console.error);
	};
	const handleOpenDeleteConfirmation = () => {
		handleOnCloseMenu();
		if (type === "folder") {
			dispatchOpenConfirmDeleteDialog();
		} else if (version > 1) {
			dispatchOpenDeleteLastVersionDialog();
		} else if (endsWithOneOf(`.${type}`, FILE_TYPES_EXTENSIONS)) {
			dispatchOpenConfirmDeleteDialog();
		} else {
			handleDelete(false);
		}
	};
	const handleCloseLastVersionDialog = () => dispatchOpenDeleteLastVersionDialog();
	const isFile = type !== "folder";
	const handleEdit = () => {
		setOpenRenameDialog((prev) => !prev);
		setIsEditingMetadata(true);
	};
	const handleChangeSelectedTab = (_, tab) => {
		if (tab === 0) {
			dispatch(setRefreshSelectedDocument(true));
		}
		setSelectedTab(tab);
	};
	return (
		<SidePanel offset={10} open={open} size={75} onClose={handleClose}>
			<SidePanelContent
				closeButtonOnRight
				secondaryActions={
					<span className={styles.secondaryActions}>
						{hasActionPermission && (
							<CustomMenuButton
								ref={dropDownControlRef}
								icon={icon.faEllipsisH}
								iconColor="var(--grey-700)"
								size="sm"
								variant="outlined"
							>
								<CustomTooltip
									arrow
									showWhenOverflow
									title={translate("document-center.side-panel.options")}
								>
									<MenuList ref={menuListRef} className={styles.menu}>
										{isFile &&
											isSegFeatureEnabled(SegFlags.RIGHTS_ON_DOCUMENTS) &&
											!isUnsupported &&
											isOkayFile &&
											!isOngoing && (
												<MenuItem onClick={handleRedirectToCanvas}>
													<span className={styles.menu__item}>
														<div>
															<IconComponent
																color="var(--grey-400)"
																icon={icon.faUserFriends}
															/>
														</div>
														<span className={styles.menu__text}>
															{translate("document-center.side-panel.menu.rights")}
														</span>
													</span>
												</MenuItem>
											)}
										{isFile &&
											isOkayFile &&
											hasActionPermission &&
											!isUnsupported &&
											!isOngoing &&
											isSegFeatureEnabled(SegFlags.VERSIONS) &&
											hasPermission(permissions.PROJECT_LEADER, permissions.PROJECT_MANAGER) && (
												<>
													<input
														ref={fileInputRef}
														hidden
														accept=".doc,.docx,.odt,.pdf,.rtf,.xls,.xlsm,.xlsx,.zip"
														aria-label="Upload new version"
														id="importSidePanelVersion"
														name="importSidePanelVersion"
														type="file"
														onChange={handleUploadVersion}
													/>
													<label htmlFor="importSidePanelVersion">
														<MenuItem
															disabled={selectedDocInformation?.phaseLockedOrArchived}
														>
															<span className={styles.menu__item}>
																<div>
																	<IconComponent
																		color="var(--grey-400)"
																		icon={icon.faPlus}
																		size="lg"
																	/>
																</div>
																<span className={styles.menu__text}>
																	{translate(
																		"document-center.side-panel.versions.add"
																	)}
																</span>
															</span>
														</MenuItem>
													</label>
												</>
											)}
										{hasActionPermission && (
											<MenuItem docType={docType} title={label} onClick={handleOpenRenameDialog}>
												<span className={styles.menu__item}>
													<div>
														<IconComponent color="var(--grey-400)" icon={icon.faPen} />
													</div>
													<span className={styles.menu__text}>
														{translate("document-center.side-panel.rename")}
													</span>
												</span>
											</MenuItem>
										)}
										{(environmentAllowed(["veolia"]) || hasActionPermission) && (
											<MenuItem onClick={handleDownload}>
												<span className={styles.menu__item}>
													<div>
														<IconComponent color="var(--grey-400)" icon={icon.faDownload} />
													</div>
													<span className={styles.menu__text}>
														{translate("document-center.side-panel.menu.download")}
													</span>
												</span>
											</MenuItem>
										)}
										{hasActionPermission && !isOngoing && (
											<MenuItem onClick={handleOpenMoveDialog}>
												<span className={styles.menu__item}>
													<div>
														<IconComponent
															color="var(--grey-400)"
															icon={icon.faArrowRight}
														/>
													</div>
													<span className={styles.menu__text}>
														{translate("document-center.side-panel.menu.move")}
													</span>
												</span>
											</MenuItem>
										)}
										{hasActionPermission && <HorizontalDivider />}
										{hasActionPermission && (
											<MenuItem onClick={handleOpenDeleteConfirmation}>
												<span className={styles.menu__item}>
													<div>
														<IconComponent color="var(--grey-400)" icon={icon.faTrash} />
													</div>
													<span className={styles.menu__text}>
														{version > 1
															? translate(
																	"document-center.side-panel.menu.delete-last-version"
															  )
															: translate("document-center.side-panel.menu.delete")}
													</span>
												</span>
											</MenuItem>
										)}
									</MenuList>
								</CustomTooltip>
							</CustomMenuButton>
						)}
					</span>
				}
				title={
					<DocumentCenterPanelTitle
						docType={environmentSwitch({ veolia: undefined, main: docType })}
						title={label}
					/>
				}
				onClose={handleClose}
			>
				<ViewTabs selectedTab={selectedTab} tabs={tabs} onChange={handleChangeSelectedTab} />
				{documentTabIndex !== -1 && (
					<ViewTabPanel
						noPadding
						className={styles.tabPanel}
						data-is-unsupported={isUnsupported}
						index={documentTabIndex}
						value={selectedTab}
					>
						<DocumentCenterDocViewerTab
							isFile={isFile}
							previousPath={location.pathname}
							redirectUrl={redirectUrl}
							selectedDocument={selectedDocument}
							status={status}
						/>
					</ViewTabPanel>
				)}
				<ViewTabPanel
					noPadding
					className={styles.tabPanel}
					data-is-unsupported={isUnsupported}
					index={informationTabIndex}
					value={selectedTab}
				>
					{isUnsupported && <UnsupportedDocumentBanner />}
					<DocumentCenterInformationTab
						hasActionPermission={hasActionPermission}
						id={id}
						selectedDocInformation={selectedDocInformation}
						status={status}
						type={type}
						onEdit={handleEdit}
						onSetDirectoryId={setDirectoryId}
						onSetSelectedDocInformation={setSelectedDocInformation}
					/>
				</ViewTabPanel>
				{isFile && versionsTabIndex !== -1 && (
					<ViewTabPanel
						noPadding
						className={styles.tabPanel}
						data-is-unsupported={isUnsupported}
						index={versionsTabIndex}
						value={selectedTab}
					>
						{isUnsupported && <UnsupportedDocumentBanner />}
						<DocumentCenterVersionTab
							id={id}
							isOngoing={isOngoing}
							label={label}
							phaseLocked={selectedDocInformation?.phaseLockedOrArchived}
							type={type}
							onAddVersion={handleUploadVersion}
						/>
					</ViewTabPanel>
				)}
				<SaveFileDialog
					fileId={id}
					isEditingMetadata={isEditingMetadata}
					isUnsupported={isUnsupported}
					open={openRenameDialog}
					type={isFile ? "file" : "folder"}
					value={{
						label,
						documentAttributes: selectedDocInformation?.documentAttributeDTOs || [],
					}}
					onClose={handleCloseRenameDialog}
				/>
				<MoveDialog
					include={false}
					numberOfElements={1}
					open={openMoveDialog}
					selectedFolders={isFile ? [] : [id]}
					onClose={dispatchOpenMoveDialog}
					onMove={handleMove}
				/>
				<ConfirmDeleteDialog
					isFolder={!isFile}
					open={openConfirmDeleteDialog}
					text={translate(
						isFile
							? "document-center.table.confirm-delete.text.file"
							: "document-center.table.confirm-delete.text.folder"
					)}
					textSubDir={translate("document-center.table.confirm-delete.sub-text.folder")}
					onClose={dispatchOpenConfirmDeleteDialog}
					onConfirm={handleDelete}
				/>
				<DeleteLastVersionDialog
					open={openDeleteLastVersionDialog}
					onClose={handleCloseLastVersionDialog}
					onConfirm={handleDelete}
				/>
				<CustomDialog
					loader
					maxWidth="xs"
					open={downloading}
					subTitle={translate("document-center.download-process-dialog.file.helper-text")}
					title={translate("document-center.download-process-dialog.file.main-text")}
				/>
			</SidePanelContent>
		</SidePanel>
	);
}
